/**
 * newSave() — a structurally valid initial Save.
 *
 * Deliberately minimal and pure: it constructs the empty-world skeleton (no team
 * unless one is supplied, empty boxes/dex/ranch/society/lore) at the current schema
 * version. The new-game flow (scenes/society) is responsible for seeding the starter
 * mon, the populated town, and the opening codex — this layer only owns the on-disk
 * shape. Randomness (the default world seed) is the only non-determinism, and it is
 * isolated so callers can pin it.
 */
import type { Save, PlayerAvatar, PokemonInstance, Settings } from '@/shared';
import { SCHEMA_VERSION, emptyQuestState, emptyCareState, emptyLeagueState } from '@/shared';

/** Optional overrides for a fresh save (new-game flow supplies the real values). */
export interface NewSaveOptions {
  readonly name?: string;
  readonly starterSpeciesId?: string;
  /** The player's chosen Showdown trainer avatar. Defaults to {@link DEFAULT_AVATAR}. */
  readonly avatar?: PlayerAvatar;
  /**
   * Species id of the follower walking behind the player. Omitted ⇒ the first team
   * member (the starter) when a team is supplied, else null.
   */
  readonly followerSpeciesId?: string | null;
  /** Pin the determinism anchor; omitted ⇒ a fresh 32-bit seed. */
  readonly worldSeed?: number;
  readonly inGameDate?: string;
  /** Wall-clock ms; injectable for deterministic tests. */
  readonly now?: number;
  /**
   * Stable per-save slot identity (D16: multi-slot saves). Omitted ⇒ `slot-<now>`.
   * The session mints a unique one per New Game so a new save never overwrites
   * another.
   */
  readonly slotId?: string;
  /** The starting team (the new-game flow builds the starter instance). */
  readonly team?: readonly PokemonInstance[];
  /** Override any settings; the rest fall back to the cozy defaults. */
  readonly settings?: Partial<Settings>;
}

/** Default trainer avatar ('red') when the new-game flow supplies none. */
export const DEFAULT_AVATAR: PlayerAvatar = { trainerKey: 'red' };

/** Calm-by-default settings (cozy difficulty, LLM on, no reduced motion). */
export const DEFAULT_SETTINGS: Settings = {
  difficulty: 'cozy',
  llmEnabled: true,
  dailyTokenBudget: 50_000,
  reducedMotion: false,
  uiScale: 1,
};

/** A fresh unsigned 32-bit world seed (the determinism anchor). */
function randomSeed(): number {
  return Math.floor(Math.random() * 0x1_0000_0000) >>> 0;
}

export function newSave(opts: NewSaveOptions = {}): Save {
  const now = opts.now ?? Date.now();
  const team = opts.team ?? [];
  const starterSpeciesId = opts.starterSpeciesId ?? 'bulbasaur';

  // A chosen starter is, by definition, both seen and caught. When no starter is
  // explicitly picked we leave the dex empty (the new-game flow seeds it on choice).
  const starterChosen = opts.starterSpeciesId !== undefined;
  const seen = starterChosen ? [starterSpeciesId] : [];
  const caught = starterChosen ? [starterSpeciesId] : [];

  // The follower defaults to the first team member (the starter) so the human player
  // is accompanied from the first step; null when there is no team yet.
  const followerSpeciesId =
    opts.followerSpeciesId !== undefined ? opts.followerSpeciesId : (team[0]?.speciesId ?? null);

  return {
    schemaVersion: SCHEMA_VERSION,
    profile: {
      id: 'player',
      slotId: opts.slotId ?? `slot-${now}`,
      name: opts.name ?? 'Trainer',
      starterSpeciesId,
      avatar: opts.avatar ?? DEFAULT_AVATAR,
      followerSpeciesId,
      createdAt: now,
    },
    team: [...team],
    boxes: [{ id: 'box-1', name: 'Box 1', contents: [] }],
    dex: { seen, caught },
    ranch: { berryPlots: [], campMemberIds: [], eggs: [] },
    society: { trainers: [], playerGuildIds: [] },
    lore: { entries: [], readEntryIds: [] },
    quests: emptyQuestState(),
    care: emptyCareState(),
    league: emptyLeagueState(),
    rival: null,
    overworld: null,
    worldSeed: opts.worldSeed ?? randomSeed(),
    inGameDate: opts.inGameDate ?? '0001-01-01',
    lastPlayedAt: now,
    settings: { ...DEFAULT_SETTINGS, ...opts.settings },
  };
}
